import React, { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  Badge, Button, Col, Input, ListGroup, ListGroupItem, Modal, ModalBody, ModalFooter,
  ModalHeader, Row, Spinner, Table,
} from "reactstrap";
import ExpenseManagerService from "../../../Services/expenseManager/ExpenseManagerService";
import { formatMoney } from "../../../utils/formatMoney";
import { getApiErrorMessage } from "../../../utils/apiErrorMessage";
import {
  accountingStatusMeta, can, expenseLabel, formatShortDate, operationLabel, paymentStatusMeta, PERMISSIONS, statusMeta,
} from "./expenseManagerUtils";
import ExpenseFormModal from "./ExpenseFormModal";

const Detail = ({ label, value }) => (
  <div className="em-detail">
    <div className="em-detail-label">{label}</div>
    <div className="em-detail-value">{value || "—"}</div>
  </div>
);

export default function ExpenseDetailModal({ isOpen, expenseId, meta, toggle, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [linking, setLinking] = useState(false);
  const [entryId, setEntryId] = useState("");
  const [candidateQuery, setCandidateQuery] = useState("");
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [payment, setPayment] = useState({ amount: "", paid_at: "", payment_method: "", reference: "", note: "" });
  const [actionBusy, setActionBusy] = useState(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const query = useQuery({
    queryKey: ["expenseManagerExpense", expenseId],
    queryFn: () => ExpenseManagerService.getExpense(expenseId),
    enabled: isOpen && Boolean(expenseId),
  });
  const expense = query.data?.data;

  const candidatesQuery = useQuery({
    queryKey: ["expenseManagerGlCandidates", expense?.company_id, candidateQuery],
    queryFn: () => ExpenseManagerService.getGlCandidates({
      company_id: expense.company_id,
      q: candidateQuery || undefined,
    }),
    enabled: (posting || linking) && Boolean(expense?.company_id),
    staleTime: 30_000,
  });

  const balanceDue = useMemo(() => {
    if (!expense) return 0;
    return Math.max(0, Number(expense.gross_amount) - Number(expense.amount_paid || 0));
  }, [expense]);

  if (!isOpen) return null;

  const refresh = () => {
    query.refetch();
    if (onChanged) onChanged();
  };

  const deleteDraft = async () => {
    setActionBusy("delete");
    try {
      await ExpenseManagerService.deleteExpense(expenseId);
      toast.success("Expense deleted.");
      toggle();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not delete the expense."));
    } finally {
      setActionBusy(null);
    }
  };

  const runAction = async (payload) => {
    setActionBusy(payload.action);
    try {
      await ExpenseManagerService.expenseAction(expenseId, payload);
      toast.success(`Expense ${payload.action} carried out.`);
      setReason("");
      setRejecting(false);
      setReversing(false);
      setPosting(false);
      setEntryId("");
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, `Could not ${payload.action} the expense.`));
    } finally {
      setActionBusy(null);
    }
  };

  const submitPayment = async () => {
    setActionBusy("payment");
    try {
      await ExpenseManagerService.addPayment(expenseId, {
        amount: payment.amount,
        paid_at: payment.paid_at,
        payment_method: payment.payment_method || undefined,
        reference: payment.reference || undefined,
        note: payment.note || undefined,
      });
      toast.success("Payment recorded.");
      setPayment({ amount: "", paid_at: "", payment_method: "", reference: "", note: "" });
      setPaymentOpen(false);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not record the payment."));
    } finally {
      setActionBusy(null);
    }
  };

  const submitLink = async (unlink = false) => {
    setActionBusy(unlink ? "unlink" : "link");
    try {
      await ExpenseManagerService.linkGl(expenseId, { entry_id: Number(entryId), unlink });
      toast.success(unlink ? "GL movement unlinked." : "GL movement linked; expense settled.");
      setLinking(false);
      setEntryId("");
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not update the GL link."));
    } finally {
      setActionBusy(null);
    }
  };

  const uploadFile = async (file, kind) => {
    setUploading(true);
    try {
      await ExpenseManagerService.uploadAttachment(expenseId, file, kind);
      toast.success("Attachment uploaded.");
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not upload the file."));
    } finally {
      setUploading(false);
    }
  };

  const deleteFile = async (attachment) => {
    setActionBusy(`del-${attachment.id}`);
    try {
      await ExpenseManagerService.deleteAttachment(expenseId, attachment.id);
      refresh();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not delete the file."));
    } finally {
      setActionBusy(null);
    }
  };

  const downloadFile = async (attachment) => {
    try {
      const response = await ExpenseManagerService.downloadAttachment(expenseId, attachment.id);
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.original_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Could not download the file."));
    }
  };

  const status = expense.status;
  const accounting = expense.accounting_status;
  const payments = expense.payments || [];
  const attachments = expense.attachments || [];
  const activity = expense.activity || [];
  const glEntry = expense.gl_entry;

  const canEdit = can(meta, PERMISSIONS.EDIT) && ["draft", "pending_approval", "rejected"].includes(status) && accounting === "none";
  const canSubmit = ["draft", "rejected"].includes(status);
  const canApprove = status === "pending_approval";
  const canCancel = ["draft", "pending_approval"].includes(status);
  const canDelete = ["draft", "pending_approval", "rejected", "cancelled"].includes(status) && accounting === "none";
  const canRecordPayment = ["approved", "paid"].includes(status);
  const canPost = status === "approved" && accounting === "none";
  const canReverse = accounting === "posted";

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" scrollable backdrop="static">
      <ModalHeader toggle={toggle}>
        <div className="d-flex align-items-center gap-2">
          <span>{expenseLabel(expense)}</span>
          {expense && (
            <div className="d-flex gap-1">
              <Badge color={statusMeta(expense.status).color} pill>{statusMeta(expense.status).label}</Badge>
              <Badge color={paymentStatusMeta(expense.payment_status).color} pill>{paymentStatusMeta(expense.payment_status).label}</Badge>
              <Badge color={accountingStatusMeta(expense.accounting_status).color} pill>{accountingStatusMeta(expense.accounting_status).label}</Badge>
            </div>
          )}
        </div>
      </ModalHeader>
      <ModalBody>
        {query.isLoading && <div className="text-center py-4"><Spinner /></div>}
        {query.isError && (
          <div className="alert alert-danger">{getApiErrorMessage(query.error, "Could not load the expense.")}</div>
        )}
        {expense && (
          <>
            <Row className="g-3 mb-2">
              <Col md={4}>
                <Detail label="Reference" value={expense.reference} />
                <Detail label="Supplier" value={expense.supplier_name || expense.counterparty?.display_name} />
                <Detail label="Company" value={expense.company?.name} />
                <Detail label="Category" value={expense.category?.name || expense.category_name} />
              </Col>
              <Col md={4}>
                <Detail label="Expense date" value={formatShortDate(expense.expense_date)} />
                <Detail label="Due date" value={formatShortDate(expense.due_date)} />
                <Detail label="Department" value={expense.department} />
                <Detail label="Cost center" value={expense.cost_center} />
              </Col>
              <Col md={4}>
                <Detail label="Net" value={formatMoney(expense.net_amount, expense.currency_code)} />
                <Detail label="Tax" value={formatMoney(expense.tax_amount, expense.currency_code)} />
                <Detail label="Gross" value={formatMoney(expense.gross_amount, expense.currency_code)} />
                <Detail label="Paid" value={formatMoney(expense.amount_paid, expense.currency_code)} />
              </Col>
            </Row>
            {expense.description && <p className="text-muted small">{expense.description}</p>}

            <div className="em-section-title">Workflow</div>
            <Row className="g-2 mb-3">
              <Col lg={3}><Detail label="Operation / GL mapping" value={operationLabel(expense.gl_operation_type)} /></Col>
              <Col lg={3}>
                <Detail
                  label="GL posting reference"
                  value={expense.gl_posting_reference || (expense.gl_entry_id ? `gl_entry #${expense.gl_entry_id}` : null)}
                />
              </Col>
              <Col lg={3}>
                <Detail
                  label="Approved"
                  value={expense.approved_at ? formatShortDate(expense.approved_at) : null}
                />
              </Col>
              <Col lg={3}><Detail label="Rejection reason" value={expense.rejection_reason} /></Col>
            </Row>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {canEdit && <Button size="sm" color="secondary" onClick={() => setEditing(true)}>Edit</Button>}
              {canSubmit && can(meta, PERMISSIONS.EDIT) && (
                <Button size="sm" color="info" onClick={() => runAction({ action: "submit" })} disabled={Boolean(actionBusy)}>
                  {actionBusy === "submit" ? "…" : "Submit for approval"}
                </Button>
              )}
              {canApprove && can(meta, PERMISSIONS.APPROVE) && (
                <Button size="sm" color="success" onClick={() => runAction({ action: "approve" })} disabled={Boolean(actionBusy)}>
                  {actionBusy === "approve" ? "…" : "Approve"}
                </Button>
              )}
              {canApprove && can(meta, PERMISSIONS.APPROVE) && (
                <Button size="sm" color="danger" outline onClick={() => setRejecting((value) => !value)}>Reject</Button>
              )}
              {rejecting && (
                <div className="d-flex gap-2 align-items-center w-100">
                  <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} />
                  <Button size="sm" color="danger" onClick={() => reason.trim() && runAction({ action: "reject", reason: reason.trim() })} disabled={!reason.trim()}>
                    Confirm reject
                  </Button>
                </div>
              )}
              {canCancel && can(meta, PERMISSIONS.MANAGE_CATEGORIES) && (
                <Button size="sm" color="dark" outline onClick={() => window.confirm("Cancel this expense?") && runAction({ action: "cancel" })}>
                  Cancel expense
                </Button>
              )}
              {canRecordPayment && can(meta, PERMISSIONS.PAY) && (
                <Button size="sm" color="primary" outline onClick={() => setPaymentOpen((value) => !value)}>
                  {paymentOpen ? "Close payment" : "Record payment"}
                </Button>
              )}
              {canPost && can(meta, PERMISSIONS.POST) && (
                <Button size="sm" color="primary" onClick={() => setPosting((value) => !value)}>Post to GL</Button>
              )}
              {canReverse && can(meta, PERMISSIONS.POST) && (
                <Button size="sm" color="dark" onClick={() => setReversing((value) => !value)}>Reverse posting</Button>
              )}
              {canDelete && can(meta, PERMISSIONS.DELETE) && (
                <Button size="sm" color="danger" outline onClick={() => window.confirm("Delete this draft?") && deleteDraft()}>
                  Delete
                </Button>
              )}
            </div>

            {paymentOpen && (
              <div className="em-inline-panel mb-3">
                <Row className="g-2">
                  <Col md={2}><Input type="number" step="0.001" min="0" placeholder="Amount" value={payment.amount} onChange={(e) => setPayment((p) => ({ ...p, amount: e.target.value }))} aria-label="Payment amount" /></Col>
                  <Col md={2}><Input type="date" value={payment.paid_at} onChange={(e) => setPayment((p) => ({ ...p, paid_at: e.target.value }))} aria-label="Payment date" /></Col>
                  <Col md={2}>
                    <Input type="select" value={payment.payment_method} onChange={(e) => setPayment((p) => ({ ...p, payment_method: e.target.value }))} aria-label="Payment method">
                      <option value="">Method…</option>
                      {meta.payment_methods.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </Input>
                  </Col>
                  <Col md={2}><Input placeholder="Reference" value={payment.reference} onChange={(e) => setPayment((p) => ({ ...p, reference: e.target.value }))} aria-label="Payment reference" /></Col>
                  <Col md={2}><Input placeholder="Note" value={payment.note} onChange={(e) => setPayment((p) => ({ ...p, note: e.target.value }))} aria-label="Payment note" /></Col>
                  <Col md={2} className="d-flex align-items-center">
                    <Button size="sm" color="success" disabled={!payment.amount || Number(payment.amount) <= 0 || actionBusy === "payment"} onClick={submitPayment}>
                      {actionBusy === "payment" ? "Saving…" : `Record (${formatMoney(balanceDue, expense.currency_code)} open)`}
                    </Button>
                  </Col>
                </Row>
              </div>
            )}

            {posting && (
<div className="em-inline-panel mb-3">
                  <div className="small text-muted mb-2">Select the bank movement the expense refers to (accrual recognition), or post without one.</div>
                  <div className="d-flex gap-2 mb-2">
                    <Input placeholder="Search label / counterparty / reference" value={candidateQuery} onChange={(e) => setCandidateQuery(e.target.value)} aria-label="Search GL movements" />
                  </div>
                  <GlCandidateList query={candidatesQuery} entryId={entryId} onSelect={setEntryId} currency={expense.currency_code} />
                  <div className="mt-2 d-flex gap-2">
                    <Button size="sm" color="primary" outline disabled={actionBusy === "post"} onClick={() => runAction({ action: "post" })}>
                      {actionBusy === "post" ? "Posting…" : "Post to GL"}
                    </Button>
                    <Button size="sm" color="primary" disabled={!entryId || actionBusy === "post"} onClick={() => runAction({ action: "post", entry_id: Number(entryId) })}>
                      Post to selected movement
                    </Button>
                  </div>
                </div>
            )}

            {reversing && (
              <div className="em-inline-panel mb-3">
                <div className="small text-muted mb-2">Reverse the GL posting (keeps the expense and its payments).</div>
                <Row className="g-2">
                  <Col md={8}><Input placeholder="Reason (optional)" value={reason} onChange={(e) => setReason(e.target.value)} /></Col>
                  <Col md={4}>
                    <Button size="sm" color="dark" onClick={() => runAction({ action: "reverse", reason: reason || undefined })} disabled={Boolean(actionBusy)}>Confirm reversal</Button>
                  </Col>
                </Row>
              </div>
            )}

            <Row className="g-3">
              <Col lg={5}>
                <div className="em-section-title">Payments</div>
                <Table size="sm" className="mb-3">
                  <thead>
                    <tr><th>Date</th><th>Method</th><th className="text-end">Amount</th><th>Reference</th></tr>
                  </thead>
                  <tbody>
                    {payments.length === 0 && <tr><td colSpan="4" className="text-muted">No payments recorded.</td></tr>}
                    {payments.map((p) => (
                      <tr key={p.id}>
                        <td>{formatShortDate(p.paid_at)}</td>
                        <td>{p.payment_method || "—"}</td>
                        <td className="text-end">{formatMoney(p.amount, p.currency_code)}</td>
                        <td>{p.reference || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <div className="em-section-title">Accounting</div>
                {expense.company && can(meta, PERMISSIONS.MANAGE_CATEGORIES) && (
                  <div className="d-flex flex-wrap gap-2 mb-2">
                    <Button size="sm" color="info" outline onClick={() => setLinking((value) => !value)}>
                      {linking ? "Close movement picker" : glEntry ? "Change movement" : "Link to bank movement"}
                    </Button>
                    {glEntry && (
                      <Button size="sm" color="danger" outline onClick={() => submitLink(true)} disabled={Boolean(actionBusy)}>Unlink movement</Button>
                    )}
                  </div>
                )}
                {glEntry && (
                  <div className="small mb-2">
                    <strong>Linked movement:</strong> {formatShortDate(glEntry.booking_date)} — {glEntry.label || glEntry.counterparty}
                    <div className="text-muted">{glEntry.operation_type ? operationLabel(glEntry.operation_type) : ""} · {formatMoney(glEntry.debit || glEntry.credit, glEntry.currency_code)}</div>
                  </div>
                )}
                {linking && (
                  <div className="d-flex gap-2 mb-2">
                    <Input placeholder="Search label / counterparty / reference" value={candidateQuery} onChange={(e) => setCandidateQuery(e.target.value)} aria-label="Search GL movements" />
                  </div>
                )}
                {linking && <GlCandidateList query={candidatesQuery} entryId={entryId} onSelect={setEntryId} currency={expense.currency_code} />}
                {linking && (
                  <Button size="sm" color="info" className="mt-2" disabled={!entryId || actionBusy === "link"} onClick={() => submitLink(false)}>
                    {actionBusy === "link" ? "Linking…" : "Link selected movement"}
                  </Button>
                )}
              </Col>

              <Col lg={4}>
                <div className="em-section-title">Attachments</div>
                {attachments.length === 0 && <div className="text-muted small mb-2">No attachments.</div>}
                <ListGroup flush className="mb-2">
                  {attachments.map((a) => (
                    <ListGroupItem key={a.id} className="d-flex align-items-center justify-content-between">
                      <span>{a.original_name}</span>
                      <span className="d-flex gap-1">
                        <Button size="sm" color="link" onClick={() => downloadFile(a)}>Open</Button>
                        {can(meta, PERMISSIONS.UPLOAD) && (
                          <Button size="sm" color="link" onClick={() => window.confirm(`Delete ${a.original_name}?`) && deleteFile(a)}>
                            Delete
                          </Button>
                        )}
                      </span>
                    </ListGroupItem>
                  ))}
                </ListGroup>
                {can(meta, PERMISSIONS.UPLOAD) && (
                  <div className="d-flex gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      className="d-none"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const ext = (file.name.split(".").pop() || "").toLowerCase();
                          const kind = ["pdf", "jpg", "jpeg", "png"].includes(ext) ? "document" : "other";
                          uploadFile(file, kind);
                        }
                        e.target.value = "";
                      }}
                    />
                    <Button size="sm" color="light" onClick={() => fileRef.current?.click()} disabled={uploading}>
                      {uploading ? "Uploading…" : "Upload file"}
                    </Button>
                  </div>
                )}
              </Col>

              <Col lg={3}>
                <div className="em-section-title">Activity</div>
                <ListGroup flush>
                  {activity.length === 0 && <ListGroupItem className="text-muted">No activity yet.</ListGroupItem>}
                  {activity.map((a) => (
                    <ListGroupItem key={a.id} className="em-activity">
                      <div>{a.action.replace(/_/g, " ")}{a.user ? <span className="text-muted"> — {a.user.name || a.user.email}</span> : null}</div>
                      {a.note ? <div className="text-muted small">{a.note}</div> : null}
                    </ListGroupItem>
                  ))}
                </ListGroup>
              </Col>
            </Row>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={toggle}>Close</Button>
      </ModalFooter>
      {editing && (
        <ExpenseFormModal
          isOpen={editing}
          expense={expense}
          meta={meta}
          toggle={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            refresh();
          }}
        />
      )}
    </Modal>
  );
}

function GlCandidateList({ query, entryId, onSelect, currency }) {
  if (query.isLoading) return <div><Spinner size="sm" /></div>;
  if (query.isError) return <div className="alert alert-warning small py-1">{getApiErrorMessage(query.error, "Could not load movements.")}</div>;
  const rows = query.data?.data || [];
  if (rows.length === 0) return <div className="text-muted small">No unlinked movements found.</div>;

  return (
    <div className="em-candidate-list">
      {rows.map((row) => (
        <label key={row.id} className={`em-candidate ${entryId === String(row.id) ? "selected" : ""}`}>
          <input
            type="radio"
            name="gl-candidate"
            checked={entryId === String(row.id)}
            onChange={() => onSelect(String(row.id))}
          />
          <span className="fw-semibold">{formatShortDate(row.booking_date)}</span>
          <span className="flex-grow-1">{row.label || row.counterparty || `#${row.id}`}</span>
          <span className="text-muted">{row.operation_type || ""}</span>
          <span className="text-end">{formatMoney(row.debit || row.credit, row.currency_code || currency)}</span>
        </label>
      ))}
    </div>
  );
}